// Guided-tour overlay (coachmark) — prefix `tov-`. Aggregated into componentStyles.
// Geometry (top/left/width/height of the spotlight + bubble position) is supplied
// inline as runtime-computed values; everything else is token-driven here.
export const tourStyles = `
  /* Full-viewport transparent catcher — makes the page inert during a tour. The
     dimming itself is painted by the spotlight's box-shadow, not this layer. */
  .tov-mask {
    position: fixed;
    inset: 0;
    z-index: var(--z-tour);
    cursor: default;
  }

  /* Spotlight cutout: a transparent box whose huge box-shadow dims everything
     around it. pointer-events:none so the mask beneath keeps the page inert. */
  .tov-spot {
    position: fixed;
    z-index: calc(var(--z-tour) + 1);
    border-radius: var(--rad-catchup-large);
    box-shadow: 0 0 0 9999px rgba(15, 72, 89, 0.55);
    outline: 2px solid var(--catchup-white);
    outline-offset: 2px;
    pointer-events: none;
    transition:
      top var(--duration-base) var(--ease-glass),
      left var(--duration-base) var(--ease-glass),
      width var(--duration-base) var(--ease-glass),
      height var(--duration-base) var(--ease-glass);
  }

  /* Glass tooltip bubble */
  .tov-bubble {
    position: fixed;
    z-index: calc(var(--z-tour) + 2);
    box-sizing: border-box;
    /* Portaled to <body>, so it doesn't inherit the app font — set it explicitly. */
    font-family: var(--font-sans);
    width: max-content;
    max-width: min(360px, calc(100vw - 32px));
    padding: var(--space-5);
    border-radius: var(--rad-catchup-large);
    background: var(--glass-popover-bg);
    backdrop-filter: var(--glass-popover-filter);
    -webkit-backdrop-filter: var(--glass-popover-filter);
    border: 1px solid var(--glass-border);
    box-shadow: var(--glass-float);
    color: var(--catchup-gray-600);
    /* hidden until measured + placed (two-pass) to avoid flicker */
    opacity: 0;
    transform: translateY(4px);
    transition:
      opacity var(--duration-base) var(--ease-glass),
      transform var(--duration-base) var(--ease-glass);
  }
  .tov-bubble--ready {
    opacity: 1;
    transform: translateY(0);
  }

  .tov-x {
    position: absolute;
    top: var(--space-3);
    right: var(--space-3);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: var(--catchup-gray-400);
    font-size: var(--fs-md);
    line-height: 1;
    cursor: pointer;
    border-radius: var(--rad-catchup-full);
  }
  .tov-x:hover { color: var(--catchup-gray-600); background: rgba(15, 72, 89, 0.08); }

  .tov-title {
    margin: 0 var(--space-5) var(--space-2) 0;
    font-size: var(--fs-lg);
    font-weight: var(--font-weight-bold, 700);
    color: var(--brand-dark, var(--catchup-blue));
  }
  .tov-body {
    margin: 0;
    font-size: var(--fs-md);
    line-height: var(--leading-base, 1.5);
    color: var(--catchup-gray-500);
  }

  .tov-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin-top: var(--space-4);
  }
  .tov-dots {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .tov-dot {
    width: 7px;
    height: 7px;
    border-radius: var(--rad-catchup-full);
    background: rgba(15, 72, 89, 0.2);
    transition: background var(--duration-base) var(--ease-standard),
      width var(--duration-base) var(--ease-standard);
  }
  .tov-dot.is-active {
    width: 18px;
    background: var(--catchup-blue);
  }

  .tov-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .tov-skip {
    border: none;
    background: transparent;
    color: var(--catchup-gray-400);
    font-size: var(--fs-sm);
    cursor: pointer;
    padding: var(--space-1) var(--space-2);
    border-radius: var(--rad-catchup-medium);
  }
  .tov-skip:hover { color: var(--catchup-gray-600); }
  .tov-btn {
    border: none;
    cursor: pointer;
    font-size: var(--fs-sm);
    font-weight: 600;
    padding: var(--space-2) var(--space-4);
    border-radius: var(--rad-catchup-full);
    transition: filter var(--duration-base) var(--ease-standard);
  }
  .tov-btn--ghost {
    background: rgba(15, 72, 89, 0.08);
    color: var(--catchup-gray-600);
  }
  .tov-btn--primary {
    background: var(--catchup-blue);
    color: var(--catchup-white);
  }
  .tov-btn:hover { filter: brightness(0.96); }

  @media (prefers-reduced-motion: reduce) {
    .tov-spot,
    .tov-bubble,
    .tov-dot { transition: none; }
  }
`;
